import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from "lucide-react";

// ===== CONFIG =====
const TOAST_CONFIG = {
  success: {
    icon: CheckCircle2,
    iconBg: "bg-success_bg",
    iconText: "text-success",
    accent: "bg-success",
    glow: "shadow-[0_8px_32px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.06)]",
  },
  error: {
    icon: AlertCircle,
    iconBg: "bg-danger_bg",
    iconText: "text-danger",
    accent: "bg-danger",
    glow: "shadow-[0_8px_32px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.06)]",
  },
  warning: {
    icon: AlertTriangle,
    iconBg: "bg-warning_bg",
    iconText: "text-warning",
    accent: "bg-warning",
    glow: "shadow-[0_8px_32px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.06)]",
  },
  info: {
    icon: Info,
    iconBg: "bg-primary_tint",
    iconText: "text-primary",
    accent: "bg-primary",
    glow: "shadow-[0_8px_32px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.06)]",
  },
};

// ===== CONTEXT =====
const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};

// ===== PROVIDER =====
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    ({ type = "info", title, message, duration = 3000 }) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, type, title, message, duration }]);
      return id;
    },
    []
  );

  const api = Object.assign(toast, {
    success: (title, message, duration) =>
      toast({ type: "success", title, message, duration }),
    error: (title, message, duration) =>
      toast({ type: "error", title, message, duration }),
    warning: (title, message, duration) =>
      toast({ type: "warning", title, message, duration }),
    info: (title, message, duration) =>
      toast({ type: "info", title, message, duration }),
  });

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="fixed top-4 right-4 z-[9999] flex flex-col items-end gap-0">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onClose={() => remove(t.id)} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

// ===== SINGLE TOAST =====
function ToastItem({ toast, onClose }) {
  const [leaving, setLeaving] = useState(false);
  const [entered, setEntered] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(100);

  const config = TOAST_CONFIG[toast.type] || TOAST_CONFIG.info;
  const Icon = config.icon;

  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const remainingRef = useRef(toast.duration);
  const progressIntervalRef = useRef(null);
  const progressStartRef = useRef(null);
  const progressStartValueRef = useRef(100);

  const handleClose = useCallback(() => {
    setLeaving(true);
    clearTimeout(timerRef.current);
    clearInterval(progressIntervalRef.current);
    setTimeout(onClose, 250);
  }, [onClose]);

  const startTimer = useCallback((remaining) => {
    clearTimeout(timerRef.current);
    clearInterval(progressIntervalRef.current);

    startTimeRef.current = Date.now();
    remainingRef.current = remaining;

    timerRef.current = setTimeout(handleClose, remaining);

    // animate progress bar smoothly
    progressStartRef.current = Date.now();
    progressStartValueRef.current = (remaining / toast.duration) * 100;

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - progressStartRef.current;
      const newProgress = Math.max(
        0,
        progressStartValueRef.current - (elapsed / toast.duration) * 100
      );
      setProgress(newProgress);
    }, 16);
  }, [handleClose, toast.duration]);

  const pauseTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    clearInterval(progressIntervalRef.current);
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (toast.duration) startTimer(toast.duration);
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(progressIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseEnter = () => {
    setPaused(true);
    pauseTimer();
  };

  const handleMouseLeave = () => {
    setPaused(false);
    startTimer(remainingRef.current);
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative mb-2.5 w-[360px] overflow-hidden rounded-xl
        border border-border bg-surface backdrop-blur-sm
        ${config.glow}
        transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]
        ${
          leaving
            ? "translate-x-3 scale-95 opacity-0"
            : entered
            ? "translate-x-0 scale-100 opacity-100"
            : "translate-x-10 scale-95 opacity-0"
        }`}
      role="alert"
    >
      {/* Left accent bar */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-[3.5px] rounded-r-full ${config.accent}`}
      />

      <div className="flex items-start gap-3 px-4 py-3.5 pl-5">
        {/* Icon */}
        <div
          className={`flex h-[34px] w-[34px] flex-shrink-0 items-center
            justify-center rounded-xl ${config.iconBg} ${config.iconText}
            ring-1 ring-inset ring-white/10`}
        >
          <Icon size={18} strokeWidth={2} />
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-[13.5px] font-semibold tracking-tight text-text_primary">
            {toast.title}
          </p>
          {toast.message && (
            <p className="mt-0.5 text-[12px] leading-relaxed text-text_secondary">
              {toast.message}
            </p>
          )}
        </div>

        {/* Close */}
        <button
          onClick={handleClose}
          aria-label="Dismiss"
          className="flex h-[24px] w-[24px] flex-shrink-0 items-center
            justify-center rounded-lg text-text_muted
            hover:bg-surface_alt hover:text-text_primary
            transition-all duration-150 active:scale-90"
        >
          <X size={13} strokeWidth={2.5} />
        </button>
      </div>

      {/* Progress bar */}
      {toast.duration && (
        <div className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-border/40">
          <div
            className={`h-full ${config.accent} transition-none`}
            style={{
              width: `${progress}%`,
              opacity: paused ? 0.5 : 1,
              transition: paused ? "opacity 0.2s" : "opacity 0.2s",
            }}
          />
        </div>
      )}

      {/* Subtle hover overlay */}
      <div
        className={`pointer-events-none absolute inset-0 rounded-xl
          bg-white/[0.015] transition-opacity duration-200
          ${paused ? "opacity-100" : "opacity-0"}`}
      />
    </div>
  );
}
